// OnboardingStep2.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import {
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface OnboardingStep2Props {
  title: string;
  subtitle: string;
}

const OnboardingStep2 = ({ title, subtitle }: OnboardingStep2Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const { modalStyle, modalClassName } = useModalHeight();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();

  //fetch data options to display
  const { data } = useAccessibility();

  //get and store selected options for this view
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  // text input option for this step
  const [other, setOther] = useState("");

  // to be firect when submitData?.ability && data is changed
  useEffect(() => {
    if (submitData?.ability && data) {
      const selectedNames = submitData.ability.split(",");
      //find option from data that matches the dataToSubmit name
      const options = data.filter((option) =>
        selectedNames.includes(option.name),
      );

      setSelectedOptions([...options.map((e) => e.name)]);

      //find an option that is in dataToSubmit but not in data
      const otherOption = selectedNames.find(
        (option) => !data.find((o: any) => o.name === option),
      );
      setOther(otherOption || "");
    }
  }, [data, submitData]);

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountViewedApplyAccessibilityRequirementPage",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountViewedApplyAccessibilityRequirementPage",
      {},
    );
  }, []);

  const onBack = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep2BackButtonClicked",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep2BackButtonClicked",
      {},
    );
    setScreenIndex(AuthModalScreen.OnboardingStep1);
  };

  const handleOptionChange = (option: string) => {
    // console.log("option", option);
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onSkip = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep2Skip");
    AnalyticsService.trackMx("authPopupCreateAccountStep2Skip", {});

    //move to Find the places that work for you
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep2Complete");
    AnalyticsService.trackMx("authPopupCreateAccountStep2Complete", {});

    //convert selectionOptions to a comma separated string
    let selectedOptionsString = selectedOptions.map((e) => e).join(",");
    if (other.length > 0) {
      if (selectedOptionsString.length > 0) {
        selectedOptionsString = selectedOptionsString.concat(",", other);
      } else {
        selectedOptionsString = other;
      }
    }

    // console.log("selectedOptionsString", selectedOptionsString);

    setSubmitData((prev) => ({
      ...prev,
      ability: selectedOptionsString,
    }));

    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onBack}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="flex-1 overflow-y-auto px-8 md:px-16">
        <div className="pt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
          <div>
            <hr className="w-full border-border-token-brand-minimal my-3" />
          </div>
        </div>
        <div>
          <p className="title-s-semibold text-fg-neutral-strong">
            {i18n.get("doYouOrSomeoneYouKnowExperienceOrUseAnyOfTheFollowing")}
          </p>
          <p className="my-4 body-l-semibold !text-fg-brand-subtle">
            {i18n.get("selectAllApply")}
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            {data?.map((item, index) => {
              return (
                <div
                  key={index}
                  onClick={() => handleOptionChange(item.name)}
                  role="button"
                  className={`flex items-center gap-1 px-4 py-2.5 rounded-3xl ${selectedOptions.includes(item.name) ? "bg-orange-300" : "bg-violet-100"}`}
                  tabIndex={0}
                >
                  <p className="text-fg-neutral-strong label-s-medium">
                    {item.name}
                  </p>
                </div>
              );
            })}
            <div className="w-full pb-4">
              <FloatingLabelInput
                label="Add your own"
                type="text"
                value={other}
                placeholder="Type here to add your own"
                onChange={(event) => {
                  setOther(event.target.value);
                }}
                onClear={() => {
                  setOther("");
                }}
              />
            </div>
          </div>
        </div>
        <div className="mb-8 items-center justify-center flex flex-col pt-2">
          <WideButton
            type="filled"
            onClick={() => onContinue()}
            disabled={selectedOptions.length <= 0 && other.length <= 0}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={onSkip}
            variant="link"
            className="body-l-semibold underline text-links mt-6"
          >
            {i18n.get("skipForNow")}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep2;
